import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useQueryParams } from '../../context/QueryParamsContext';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import api from '../../services/api';
import therapeuticAreaConfig from '../../config/therapeuticAreaConfig.json';
import './StudyForm.css';
import StudyIcons from '../StudyIcons/StudyIcons';

// Error Icon Component
const ErrorIcon = () => (
  <span className="error-icon-wrapper" data-tooltip="This field is required">
    <svg className="error-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="7" stroke="#DC2626" strokeWidth="2" fill="none"/>
      <path d="M8 4V9" stroke="#DC2626" strokeWidth="2" strokeLinecap="round"/>
      <circle cx="8" cy="11.5" r="0.5" fill="#DC2626"/>
    </svg>
  </span>
);

// Dynamically generate iconList from therapeuticAreaConfig
// This extracts all unique icon IDs across all therapeutic areas
const iconList = (() => {
  const allIconIds = new Set();

  // Collect all icon IDs from all therapeutic areas
  Object.values(therapeuticAreaConfig).forEach(area => {
    if (area.studyIcons && Array.isArray(area.studyIcons)) {
      area.studyIcons.forEach(iconId => allIconIds.add(iconId));
    }
  });

  // Helper function to generate a readable name from icon ID
  const generateIconName = (id) => {
    // Special cases for known icons
    const nameMap = {
      'none': 'None',
      'microscope': 'Microscope',
      'eye': 'Eye',
      'checklist': 'Checklist',
      'patient': 'Patient',
      'antibody': 'Antibody',
      'retina': 'Retina',
      'eyeball': 'eyeball',
      'antibodycoatedrbc': 'Antibody-coated RBC',
      'rbc': 'RBC',
      'crenatedrbc': 'Crenated RBC',
      'clusterofcancercells': 'Cluster of Cancer Cells',
      'cancermass': 'Cancer Mass',
      'tumorcell': 'Tumor Cell',
      'nervecell': 'Nerve Cell',
      'supportbraincell': 'Support Brain Cell',
      'brain': 'Brain',
      'globe': 'Globe',
      'molecule': 'Molecule',
    };

    if (nameMap[id]) return nameMap[id];

    // For new icons, generate name from ID (e.g., "new1" -> "New Icon 1")
    const match = id.match(/^new(\d+)$/);
    if (match) {
      return `New Icon ${match[1]}`;
    }

    // Default: capitalize first letter
    return id.charAt(0).toUpperCase() + id.slice(1);
  };

  // Convert Set to array of objects with id and name
  return Array.from(allIconIds).map(id => ({
    id,
    name: generateIconName(id)
  }));
})();

const degreeOptions = [
  { value: 'MD', label: 'MD' },
  { value: 'PhD', label: 'PhD' },
  { value: 'PharmD', label: 'PharmD' },
  { value: 'DO', label: 'DO' },
  { value: 'RN', label: 'RN' },
  { value: 'NP', label: 'NP' },
  { value: 'PA', label: 'PA' },
];

// Quill Editor configuration
const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    ['link', { 'script': 'sub' }, { 'script': 'super' }, 'clean'],
  ],
};

const quillFormats = [
  'bold', 'italic', 'underline', 'strike',
  'list',
  'link', 'script',
];

// Quill config for Study Name / Title / Author (minimal toolbar)
const quillModulesMinimal = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ script: 'sub' }, { script: 'super' }]
  ],
};

const quillFormatsMinimal = ['bold', 'italic', 'underline', 'script'];

// Helper to check if image URL is valid (not a default/placeholder)
const isValidImageUrl = (url) => {
  if (!url) return false;

  // Check for React logo signature color in SVG data URLs (React's cyan color)
  if (url.includes('#61DAFB') || url.includes('%2361DAFB')) return false;

  // Exclude default React logos and common placeholder paths
  const invalidPatterns = [
    '/logo192.png',
    '/logo512.png',
    '/logo.svg',
    '/logo.png',
    'logo192.png',
    'logo512.png',
    'logo.svg',
    '/static/media/logo',  // Create React App bundled logo path
  ];

  // Also check if the URL is exactly 'logo.svg' or similar short default names
  const exactInvalidNames = ['logo.svg', 'logo.png', 'logo192.png', 'logo512.png'];
  if (exactInvalidNames.includes(url)) return false;

  return !invalidPatterns.some(pattern => url.includes(pattern));
};

const StudyForm = ({ study, onUpdate, onSave, onDelete, onBlur, isSaving, isPublished, token, jwtToken, guideId, user, isGuestUser, isNewGuide, showPopup, therapeuticArea, hasAttemptedSave, onSaveAttempt }) => {
  const [imagePreview, setImagePreview] = useState(isValidImageUrl(study.image) ? study.image : null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const fileInputRef = useRef(null);
  const [articleUrl, setArticleUrl] = useState(study.articleUrl || '');
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [articleUrlError, setArticleUrlError] = useState('');

  // Check if AI summary feature is enabled
  const isAISummaryEnabled = process.env.REACT_APP_ENABLE_AI_SUMMARY === 'true';

  // Get available study icons based on selected therapeutic area
  const availableIcons = useMemo(() => {
    if (therapeuticArea && therapeuticAreaConfig[therapeuticArea]) {
      const allowedIconIds = therapeuticAreaConfig[therapeuticArea].studyIcons;
      return iconList.filter(icon => allowedIconIds.includes(icon.id));
    }
    // Default to first 5 icons if no therapeutic area is selected
    return iconList.slice(0, 6);
  }, [therapeuticArea]);
  const { hidegemini } = useQueryParams();

  // Auto-select first icon when therapeutic area changes or when study has no icon
  useEffect(() => {
    if (therapeuticArea && availableIcons.length > 0) {
      // Only auto-select if current icon is empty or not in the available icons list
      const currentIconInList = availableIcons.find(icon => icon.id === study.icon);
      if (!study.icon || !currentIconInList) {
        // Get the first icon from the available icons list
        const firstIcon = availableIcons[0];
        if (firstIcon && firstIcon.id) {
          // Only update if the icon component exists
          const IconComponent = StudyIcons[firstIcon.id];
          if (IconComponent) {
            onUpdate('icon', firstIcon.id);
            clearFieldError('icon');
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [therapeuticArea, availableIcons, study.icon]);

  // Sync imagePreview when study.image changes (e.g., when switching studies)
  useEffect(() => {
    setImagePreview(isValidImageUrl(study.image) ? study.image : null);
  }, [study.image, study.id]);

  // Sync articleUrl when study changes (e.g., when switching studies or reopening accordion)
  useEffect(() => {
    setArticleUrl(study.articleUrl || '');
  }, [study.articleUrl, study.id]);

  // Helper function to validate study and return errors
  const validateStudy = useCallback(() => {
    const requiredFields = [
      { field: 'type', label: 'Study type' },
      { field: 'name', label: 'Study Name' },
      { field: 'icon', label: 'Study Icon' },
      { field: 'title', label: 'Study Title' },
      { field: 'author_name', label: 'Author Name and Credentials' },
      { field: 'objective', label: 'Objective' },
      { field: 'blurb', label: 'Summary of Results' },
      { field: 'references', label: 'References' },
      { field: 'presentation_link', label: 'Presentation Link', isURL: true },
      { field: 'prescribing_link', label: 'Prescribing Information Link', isURL: true },
    ];

    // Optional URL fields that should still show errors if filled with invalid URLs
    const optionalURLFields = [
      { field: 'msl_contact_link', label: 'Contact Your MSL Link', isURL: true },
    ];

    const errors = {};
    for (const { field, isURL: isURLField } of requiredFields) {
      const value = study[field];
      // Check if field is empty (handles strings, null, undefined, and empty HTML from Quill)
      if (!value || (typeof value === 'string' && (value.trim() === '' || value === '<p><br></p>'))) {
        errors[field] = true;
      } else if (isURLField && !isValidURL(value)) {
        // Additional URL validation for link fields
        errors[field] = true;
      }
    }

    // Check optional URL fields - only validate if they have a value
    for (const { field } of optionalURLFields) {
      const value = study[field];
      if (value && typeof value === 'string' && value.trim() !== '' && !isValidURL(value)) {
        errors[field] = true;
      }
    }

    return errors;
  }, [study]);

  // Validate and show errors when reopening accordion if user has previously attempted to save
  // Also validate URL fields on mount to persist URL errors when accordion reopens
  useEffect(() => {
    // Always check URL fields for invalid values (these should persist across accordion open/close)
    const urlFields = ['presentation_link', 'prescribing_link', 'msl_contact_link'];
    const urlErrors = {};

    for (const field of urlFields) {
      const value = study[field];
      if (value && typeof value === 'string' && value.trim() !== '' && !isValidURL(value)) {
        urlErrors[field] = true;
      }
    }

    // If user has attempted save, run full validation
    if (hasAttemptedSave) {
      const errors = validateStudy();
      setFieldErrors(errors);
    } else if (Object.keys(urlErrors).length > 0) {
      // Otherwise, just set URL errors if any
      setFieldErrors(prev => ({ ...prev, ...urlErrors }));
    }
  }, [hasAttemptedSave, validateStudy, study.presentation_link, study.prescribing_link, study.msl_contact_link]); // Run when hasAttemptedSave changes or study data changes

  // Clear field error when user enters a valid value
  const clearFieldError = (field) => {
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: false }));
    }
  };

  const studyTypes = [
    { value: 'LTE', label: 'Long-term Extension' },
    { value: 'RWE', label: 'Real World Experience' },
    { value: 'BM', label: 'Biomarker' },
    { value: 'Ph2', label: 'Phase 2' },
    { value: 'Ph3', label: 'Phase 3' },
  ];

  // Helper function to get icon color based on study type
  const getStudyColor = (type) => {
    switch (type) {
      case 'LTE':
        return '#0366D3';
      case 'RWE':
        return '#DB4505';
      case 'BM':
        return '#12284A';
      case 'Ph2':
        return '#AE2185';
      case 'Ph3':
        return '#460968';
      default:
        return '#0366D3'; // Default blue
    }
  };

  // Disable editing if the guide is published
  const isDisabled = isPublished;

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setIsUploadingImage(true);
      clearFieldError('image');
      try {
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64Image = reader.result;

          // On /builder/new (draft mode), store base64 locally without API call
          // Image will be uploaded when guide is saved
          if (isNewGuide || !token || !guideId) {
            onUpdate('image', base64Image);
            setImagePreview(base64Image);
            setIsUploadingImage(false);
            return;
          }

          try {
            // Upload image to server with studyimg=true flag
            const response = await api.uploadThumbnail(token, jwtToken, guideId, base64Image, user, isGuestUser, true);

            // The API should return an object with the image URL
            if (response && response.thumbnail_url) {
              // Update study with the returned URL
              onUpdate('image', response.thumbnail_url);
              setImagePreview(response.thumbnail_url);
            } else {
              console.error('No image URL returned from server');
              // Fallback to base64 if URL not returned
              onUpdate('image', base64Image);
            }
          } catch (uploadError) {
            console.error('Error uploading study image:', uploadError);
            // Keep the base64 preview but show error
            alert('Failed to upload image. Please try again.');
            setImagePreview(null);
          } finally {
            setIsUploadingImage(false);
          }
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Error reading file:', error);
        setIsUploadingImage(false);
      }
    }
  };

  // URL validation helper
  const isValidURL = (string) => {
    try {
      const url = new URL(string);
      // Check if protocol is http or https
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (_) {
      return false;
    }
  };

  // Handle article URL change - just update the value
  const handleArticleUrlChange = (e) => {
    const url = e.target.value;
    setArticleUrl(url);

    // Save the URL to the study object so it persists when accordion closes/opens
    onUpdate('articleUrl', url);

    // Clear any previous error while typing
    if (articleUrlError) setArticleUrlError('');
  };

  // Handle article URL blur - validate and call API if valid
  const handleArticleUrlBlur = async () => {
    const url = articleUrl.trim();

    // If empty, just return (it's optional)
    if (!url) return;

    // Check if URL is valid
    if (!isValidURL(url)) {
      setArticleUrlError('Please enter a valid URL (starting with http:// or https://)');
      return;
    }

    // Valid URL - call the API
    setIsGeneratingSummary(true);

    try {
      const result = await api.generateStudySummary(token, guideId, user, isGuestUser, url);

      // Map studyName to name field
      if (result.studyName) {
        onUpdate('name', `<p>${result.studyName}</p>`);
        if (fieldErrors.name) {
          setFieldErrors(prev => ({ ...prev, name: false }));
        }
      }

      // Map studyTitle to title field
      if (result.studyTitle) {
        onUpdate('title', `<p>${result.studyTitle}</p>`);
        if (fieldErrors.title) {
          setFieldErrors(prev => ({ ...prev, title: false }));
        }
      }

      // Map authors to author_name field
      if (result.authors) {
        onUpdate('author_name', `<p>${result.authors}</p>`);
        if (fieldErrors.author_name) {
          setFieldErrors(prev => ({ ...prev, author_name: false }));
        }
      }

      // Map classification to icon selection
      if (result.classification) {
        // Find matching icon based on classification
        const classificationLower = result.classification.toLowerCase();
        let selectedIconId = 'none'; // default

        // Match classification with icon names
        if (classificationLower.includes('microscope') || classificationLower.includes('laboratory')) {
          selectedIconId = 'microscope';
        } else if (classificationLower.includes('eye') || classificationLower.includes('vision') || classificationLower.includes('ophthal')) {
          selectedIconId = 'eye';
        } else if (classificationLower.includes('patient') || classificationLower.includes('clinical')) {
          selectedIconId = 'patient';
        } else if (classificationLower.includes('antibody') || classificationLower.includes('injection') || classificationLower.includes('vaccine')) {
          selectedIconId = 'antibody';
        } else if (classificationLower.includes('retina') || classificationLower.includes('neuro') || classificationLower.includes('cognitive')) {
          selectedIconId = 'retina';
        } else if (classificationLower.includes('eyeball') || classificationLower.includes('gene') || classificationLower.includes('genetic')) {
          selectedIconId = 'eyeball';
        }

        onUpdate('icon', selectedIconId);
        if (fieldErrors.icon) {
          setFieldErrors(prev => ({ ...prev, icon: false }));
        }
      }

      // Map presentationLink to presentation_link field
      if (result.presentationLink) {
        onUpdate('presentation_link', result.presentationLink);
        if (fieldErrors.presentation_link) {
          setFieldErrors(prev => ({ ...prev, presentation_link: false }));
        }
      }

      // Populate the objective field
      if (result.objective) {
        onUpdate('objective', `<p>${result.objective}</p>`);
        if (fieldErrors.objective) {
          setFieldErrors(prev => ({ ...prev, objective: false }));
        }
      }

      // Populate the blurb (Summary of Results) field, appending safetyBlurb if present
      if (result.blurb || result.safetyBlurb) {
        const blurbContent = result.blurb || '';
        const safetyContent = result.safetyBlurb || '';
        const combinedBlurb = safetyContent ? `${blurbContent}${safetyContent}` : blurbContent;
        onUpdate('blurb', combinedBlurb);
        if (fieldErrors.blurb) {
          setFieldErrors(prev => ({ ...prev, blurb: false }));
        }
      }

      // Populate the abbreviations field
      if (result.abbreviations) {
        onUpdate('abbreviations', result.abbreviations);
        if (fieldErrors.abbreviations) {
          setFieldErrors(prev => ({ ...prev, abbreviations: false }));
        }
      }

      // Populate the references field
      if (result.references) {
        onUpdate('references', result.references);
        if (fieldErrors.references) {
          setFieldErrors(prev => ({ ...prev, references: false }));
        }
      }

      // Trigger auto-save after populating fields
      if (onBlur) onBlur();

      // Show success popup after data is populated
      if (showPopup) {
        showPopup('success', 'AI summary generated successfully! Fields have been populated.');
      }

    } catch (error) {
      console.error('Error generating summary:', error);
      // Show error popup if generation fails
      if (showPopup) {
        showPopup('error', 'Failed to generate AI summary. Please try again.');
      }
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Notify parent that user attempted to save (for persisting validation state)
    if (onSaveAttempt) {
      onSaveAttempt();
    }

    // Validate all required fields
    const requiredFields = [
      { field: 'type', label: 'Study type' },
      { field: 'name', label: 'Study Name' },
      { field: 'icon', label: 'Study Icon' },
      { field: 'title', label: 'Study Title' },
      { field: 'author_name', label: 'Author Name and Credentials' },
      { field: 'objective', label: 'Objective' },
      { field: 'blurb', label: 'Summary of Results' },
      { field: 'references', label: 'References' },
      { field: 'presentation_link', label: 'Presentation Link', isURL: true },
      { field: 'prescribing_link', label: 'Prescribing Information Link', isURL: true },
    ];

    const errors = {};
    for (const { field, label, isURL } of requiredFields) {
      const value = study[field];
      // Check if field is empty (handles strings, null, undefined, and empty HTML from Quill)
      if (!value || (typeof value === 'string' && (value.trim() === '' || value === '<p><br></p>'))) {
        errors[field] = true;
      } else if (isURL && !isValidURL(value)) {
        // Additional URL validation for link fields
        errors[field] = true;
      }
    }

    // If there are errors, set them and don't submit
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      // Scroll to first error after state update
      setTimeout(() => {
        const firstErrorField = document.querySelector('.study-form .input-group.error, .study-form .quill-wrapper.error');
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 50);
      return;
    }

    // Clear errors and save
    setFieldErrors({});
    onSave();
  };

  return (
    <div className={`study-form ${isDisabled ? 'disabled' : ''}`}>
      <form onSubmit={handleSubmit} noValidate>
        <div className="form-required-notice">
          Required fields<span className="required-asterisk">*</span>
        </div>

        <div className="form-fields">

        { (hidegemini !== true && isAISummaryEnabled) && (
          <>
            <div className="input-group toggle-group">
              <label className="toggle-label">
                <div className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={study.include_gemini || false}
                    onChange={(e) => {
                      onUpdate('include_gemini', e.target.checked);
                      // Trigger auto-save with a slight delay for toggle changes
                      // This ensures state update completes before auto-save runs
                      if (onBlur) setTimeout(onBlur, 100);
                    }}
                    disabled={isDisabled}
                  />
                  <span className="toggle-slider"></span>
                </div>
                <span className="toggle-text">Include Gemini</span>
              </label>
            </div>

            {/* Article URL for AI Summary Generation */}
            {(study.include_gemini === true) && (
            <div className="input-group article-url-group">
              <label htmlFor={`article-url-${study.id}`}>
                <span><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
  <path d="M9.93548 15.5002C9.8462 15.1542 9.66581 14.8384 9.41309 14.5856C9.16037 14.3329 8.84455 14.1525 8.49848 14.0632L2.36348 12.4812C2.25881 12.4515 2.16668 12.3885 2.10109 12.3017C2.03549 12.2149 2 12.1091 2 12.0002C2 11.8914 2.03549 11.7856 2.10109 11.6988C2.16668 11.612 2.25881 11.549 2.36348 11.5192L8.49848 9.93625C8.84442 9.84706 9.16017 9.66682 9.41288 9.41429C9.66559 9.16175 9.84604 8.84614 9.93548 8.50025L11.5175 2.36525C11.5469 2.26017 11.6099 2.16759 11.6968 2.10164C11.7837 2.0357 11.8899 2 11.999 2C12.1081 2 12.2142 2.0357 12.3012 2.10164C12.3881 2.16759 12.4511 2.26017 12.4805 2.36525L14.0615 8.50025C14.1508 8.84632 14.3311 9.16215 14.5839 9.41487C14.8366 9.66759 15.1524 9.84797 15.4985 9.93725L21.6335 11.5182C21.739 11.5473 21.832 11.6103 21.8983 11.6973C21.9646 11.7844 22.0005 11.8908 22.0005 12.0002C22.0005 12.1097 21.9646 12.2161 21.8983 12.3032C21.832 12.3902 21.739 12.4531 21.6335 12.4822L15.4985 14.0632C15.1524 14.1525 14.8366 14.3329 14.5839 14.5856C14.3311 14.8384 14.1508 15.1542 14.0615 15.5002L12.4795 21.6353C12.4501 21.7403 12.3871 21.8329 12.3002 21.8989C12.2132 21.9648 12.1071 22.0005 11.998 22.0005C11.8889 22.0005 11.7827 21.9648 11.6958 21.8989C11.6089 21.8329 11.5459 21.7403 11.5165 21.6353L9.93548 15.5002Z" stroke="#12284A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M20 3V7" stroke="#12284A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M22 5H18" stroke="#12284A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M4 17V19" stroke="#12284A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M5 18H3" stroke="#12284A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg></span>
                Gemini Auto-Populate
              </label>
              <span className='article-url-description'>Add medically.gene.com URL of a study </span>
              <div className="article-url-wrapper">
                <input
                  type="url"
                  id={`article-url-${study.id}`}
                  value={articleUrl}
                  onChange={handleArticleUrlChange}
                  placeholder="https://..."
                  className={`link-input ${articleUrlError ? 'error' : ''}`}
                  disabled={isGeneratingSummary}
                />
                <button
                  type="button"
                  className="btn btn-generate-summary"
                  onClick={handleArticleUrlBlur}
                  disabled={isGeneratingSummary || !articleUrl.trim()}
                >
                  {isGeneratingSummary ? (
                    <span className="spinner small"></span>
                  ) : (
                    'Generate'
                  )}
                </button>
              </div>
              {articleUrlError && (
                <span className="error-message">{articleUrlError}</span>
              )}
            </div>
            )}
          </>
        )}

        {/* Study Type */}
        <div className={`input-group ${fieldErrors.type ? 'error' : ''}`}>
          <label>
            Study type<span className="required-asterisk">*</span>
            {fieldErrors.type && <ErrorIcon />}
          </label>
          <div className={`study-type-radio-group ${isDisabled ? 'disabled' : ''}`}>
            {studyTypes.map((type) => (
              <label key={type.value} className="radio-label">
                <input
                  type="radio"
                  name={`study-type-${study.id}`}
                  value={type.value}
                  checked={study.type === type.value}
                  onChange={() => {
                    if (!isDisabled) {
                      onUpdate('type', type.value);
                      clearFieldError('type');
                      // Trigger auto-save after update
                      if (onBlur) onBlur();
                    }
                  }}
                  disabled={isDisabled}
                  required
                />
                <span className="radio-text">{type.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Study Name */}
        <div className={`input-group ${fieldErrors.name ? 'error' : ''}`}>
          <label htmlFor={`study-name-${study.id}`}>
            Study Name<span className="required-asterisk">*</span>
            {fieldErrors.name && <ErrorIcon />}
          </label>

          <div className={`quill-wrapper ${isDisabled ? 'disabled' : ''} ${fieldErrors.name ? 'error' : ''}`}>
            <ReactQuill
              theme="snow"
              value={study.name || ''}
              onChange={(content) => {
                onUpdate('name', content);
                if (content && content !== '<p><br></p>') clearFieldError('name');
              }}
              onBlur={onBlur}
              modules={quillModulesMinimal}
              formats={quillFormatsMinimal}
              placeholder="Enter title..."
              readOnly={isDisabled}
            />
          </div>
        </div>

        {/* Select Study Icon */}
        <div className={`input-group ${fieldErrors.icon ? 'error' : ''}`}>
          <label>
            Select Study Icon<span className="required-asterisk">*</span>
            {fieldErrors.icon && <ErrorIcon />}
          </label>
          <div className="study-icons-grid">
            {availableIcons.map((icon) => {
              const IconComponent = StudyIcons[icon.id];
              // const iconColor = getStudyColor(study.type);
              return (
                <button
                  key={icon.id}
                  type="button"
                  className={`study-icon-btn ${study.icon === icon.id ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                  onClick={() => {
                    if (!isDisabled) {
                      onUpdate('icon', icon.id);
                      clearFieldError('icon');
                      // Trigger auto-save after update
                      if (onBlur) onBlur();
                    }
                  }}
                  title={icon.name}
                  disabled={isDisabled}
                >
                  <IconComponent />
                </button>
              );
            })}
          </div>
        </div>

        {/* Study Title */}
        <div className={`input-group ${fieldErrors.title ? 'error' : ''}`}>
          <label htmlFor={`study-title-${study.id}`}>
            Study Title<span className="required-asterisk">*</span>
            {fieldErrors.title && <ErrorIcon />}
          </label>

          <div className={`quill-wrapper ${isDisabled ? 'disabled' : ''} ${fieldErrors.title ? 'error' : ''}`}>
            <ReactQuill
              theme="snow"
              value={study.title || ''}
              onChange={(content) => {
                onUpdate('title', content);
                if (content && content !== '<p><br></p>') clearFieldError('title');
              }}
              onBlur={onBlur}
              modules={quillModulesMinimal}
              formats={quillFormatsMinimal}
              placeholder="Enter title..."
              readOnly={isDisabled}
            />
          </div>
        </div>

        {/* Author Name and Degree Abbreviation */}
        <div className="input-group-row">
          <div className={`input-group flex-grow ${fieldErrors.author_name ? 'error' : ''}`}>
            <label htmlFor={`author-name-${study.id}`}>
              Study Author Name(s) and Credentials<span className="required-asterisk">*</span>
              {fieldErrors.author_name && <ErrorIcon />}
            </label>

            <div className={`quill-wrapper ${isDisabled ? 'disabled' : ''} ${fieldErrors.author_name ? 'error' : ''}`}>
              <ReactQuill
                theme="snow"
                value={study.author_name || ''}
                onChange={(content) => {
                  onUpdate('author_name', content);
                  if (content && content !== '<p><br></p>') clearFieldError('author_name');
                }}
                onBlur={onBlur}
                modules={quillModulesMinimal}
                formats={quillFormatsMinimal}
                placeholder="First Last"
                readOnly={isDisabled}
              />
            </div>
          </div>
          <div className="input-group degree-group"  style={{ display: "none" }}>
            <label htmlFor={`degree-${study.id}`}>Degree Abbreviation</label>
            <div className="select-wrapper">
              <select
                id={`degree-${study.id}`}
                value={study.degree || 'MD'}
                onChange={(e) => onUpdate('degree', e.target.value)}
                onBlur={onBlur}
                disabled={isDisabled}
              >
                {degreeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <svg className="select-arrow" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
        </div>

        {/* Genentech Affiliated Authors Section - after Author Name and Credentials */}
        <div className="input-group">
          <div className="section-header-with-toggle">
            <label className="toggle-label">
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  checked={study.show_affiliated_authors !== false}
                  onChange={(e) => {
                    onUpdate('show_affiliated_authors', e.target.checked);
                    if (onBlur) setTimeout(onBlur, 100);
                  }}
                  disabled={isDisabled}
                />
                <span className="toggle-slider"></span>
              </div>
              <span className="toggle-text">Genentech Affiliated Authors</span>
            </label>
          </div>

          {study.show_affiliated_authors !== false && (
            <div className="authors-section-box">
              <div className="input-group">
                <label htmlFor={`affiliated_authors_title-${study.id}`}>Section Title</label>
                <input
                  type="text"
                  id={`affiliated_authors_title-${study.id}`}
                  value={study.affiliated_authors_title !== undefined ? study.affiliated_authors_title : 'Retina Consultants of America (RCA) affiliated author:'}
                  onChange={(e) => onUpdate('affiliated_authors_title', e.target.value)}
                  onBlur={onBlur}
                  placeholder="Retina Consultants of America (RCA) affiliated author:"
                  disabled={isDisabled}
                />
              </div>
              <div className="input-row">
                <div className="input-group">
                  <label htmlFor={`author_1_name-${study.id}`}>Author 1 Name and Credentials</label>
                  <input
                    type="text"
                    id={`author_1_name-${study.id}`}
                    value={study.author_1_name || ''}
                    onChange={(e) => onUpdate('author_1_name', e.target.value)}
                    onBlur={onBlur}
                    placeholder="e.g., John Smith"
                    disabled={isDisabled}
                  />
                </div>
                <div className="input-group">
                  <label htmlFor={`author_2_name-${study.id}`}>Author 2 Name and Credentials</label>
                  <input
                    type="text"
                    id={`author_2_name-${study.id}`}
                    value={study.author_2_name || ''}
                    onChange={(e) => onUpdate('author_2_name', e.target.value)}
                    onBlur={onBlur}
                    placeholder="e.g., Jane Doe"
                    disabled={isDisabled}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Objective with Quill Editor */}
        <div className={`input-group ${fieldErrors.objective ? 'error' : ''}`}>
          <label>
            Objective<span className="required-asterisk">*</span>
            {fieldErrors.objective && <ErrorIcon />}
          </label>
          <div className={`quill-wrapper ${isDisabled ? 'disabled' : ''} ${fieldErrors.objective ? 'error' : ''}`}>
            <ReactQuill
              theme="snow"
              value={study.objective || ''}
              onChange={(content) => {
                onUpdate('objective', content);
                if (content && content !== '<p><br></p>') clearFieldError('objective');
              }}
              onBlur={onBlur}
              modules={quillModules}
              formats={quillFormats}
              placeholder="Text box placeholder..."
              readOnly={isDisabled}
            />
          </div>
        </div>

        {/* Image Upload */}
        <div className={`input-group ${fieldErrors.image ? 'error' : ''}`}>
          <label>
            Image
            {fieldErrors.image && <ErrorIcon />}
          </label>
          <div className="image-upload-wrapper">
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              onChange={handleImageChange}
              disabled={isDisabled || isUploadingImage}
              className="file-input"
              onBlur={onBlur}
              required={!imagePreview}
            />
            <button
              type="button"
              className="choose-file-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={isDisabled || isUploadingImage}
            >
              {isUploadingImage ? 'Uploading...' : 'Choose File'}
            </button>
            <span className="file-name">
              {isUploadingImage ? 'Uploading image...' : imagePreview ? 'File selected' : 'No file chosen'}
            </span>
          </div>
          {imagePreview && !isUploadingImage && (
            <div className="image-preview">
              <img src={imagePreview} alt="Study preview" />
            </div>
          )}
        </div>

        {/* Blurb with Quill Editor */}
        <div className={`input-group ${fieldErrors.blurb ? 'error' : ''}`}>
          <label>
            Summary of Results<span className="required-asterisk">*</span>
            {fieldErrors.blurb && <ErrorIcon />}
          </label>
          <div className={`quill-wrapper ${isDisabled ? 'disabled' : ''} ${fieldErrors.blurb ? 'error' : ''}`}>
            <ReactQuill
              theme="snow"
              value={study.blurb || ''}
              onChange={(content) => {
                onUpdate('blurb', content);
                if (content && content !== '<p><br></p>') clearFieldError('blurb');
              }}
              onBlur={onBlur}
              modules={quillModules}
              formats={quillFormats}
              placeholder="Text box placeholder..."
              readOnly={isDisabled}
            />
          </div>
        </div>

        {/* References with Quill Editor */}
        <div className={`input-group ${fieldErrors.references ? 'error' : ''}`}>
          <label>
            References<span className="required-asterisk">*</span>
            {fieldErrors.references && <ErrorIcon />}
          </label>
          <div className={`quill-wrapper ${isDisabled ? 'disabled' : ''} ${fieldErrors.references ? 'error' : ''}`}>
            <ReactQuill
              theme="snow"
              value={study.references || ''}
              onChange={(content) => {
                onUpdate('references', content);
                if (content && content !== '<p><br></p>') clearFieldError('references');
              }}
              onBlur={onBlur}
              modules={quillModules}
              formats={quillFormats}
              placeholder="Text box placeholder..."
              readOnly={isDisabled}
            />
          </div>
        </div>

        {/* Abbreviations with Quill Editor */}
        <div className="input-group">
          <label>
            Abbreviations
            {fieldErrors.abbreviations && <ErrorIcon />}
          </label>
          <div className={`quill-wrapper ${isDisabled ? 'disabled' : ''} ${fieldErrors.abbreviations ? 'error' : ''}`}>
            <ReactQuill
              theme="snow"
              value={study.abbreviations || ''}
              onChange={(content) => {
                onUpdate('abbreviations', content);
                if (content && content !== '<p><br></p>') clearFieldError('abbreviations');
              }}
              onBlur={onBlur}
              modules={quillModules}
              formats={quillFormats}
              placeholder="AEs, adverse events; BCVA, best corrected visual acuity..."
              readOnly={isDisabled}
            />
          </div>
        </div>

        {/* Presentation Link */}
        <div className={`input-group ${fieldErrors.presentation_link ? 'error' : ''}`}>
          <label htmlFor={`presentation-link-${study.id}`}>
            Presentation Link<span className="required-asterisk">*</span>
            {fieldErrors.presentation_link && <ErrorIcon />}
          </label>
          <input
            type="url"
            id={`presentation-link-${study.id}`}
            value={study.presentation_link || ''}
            onChange={(e) => {
              onUpdate('presentation_link', e.target.value);
              if (e.target.value && isValidURL(e.target.value)) clearFieldError('presentation_link');
            }}
            onBlur={(e) => {
              // Validate URL on blur
              const value = e.target.value.trim();
              if (value && !isValidURL(value)) {
                setFieldErrors(prev => ({ ...prev, presentation_link: true }));
              }
              // Call parent onBlur for auto-save
              if (onBlur) onBlur();
            }}
            placeholder="https://..."
            disabled={isDisabled}
            className={`link-input ${fieldErrors.presentation_link ? 'input-error' : ''}`}
            required
          />
          {fieldErrors.presentation_link && study.presentation_link && !isValidURL(study.presentation_link) && (
            <span className="error-message">Please enter a valid URL (e.g., https://example.com)</span>
          )}
        </div>

        {/* Prescribing Information Link */}
        <div className={`input-group ${fieldErrors.prescribing_link ? 'error' : ''}`}>
          <label htmlFor={`prescribing-link-${study.id}`}>
            Prescribing Information Link<span className="required-asterisk">*</span>
            {fieldErrors.prescribing_link && <ErrorIcon />}
          </label>
          <input
            type="url"
            id={`prescribing-link-${study.id}`}
            value={study.prescribing_link || ''}
            onChange={(e) => {
              onUpdate('prescribing_link', e.target.value);
              if (e.target.value && isValidURL(e.target.value)) clearFieldError('prescribing_link');
            }}
            onBlur={(e) => {
              // Validate URL on blur
              const value = e.target.value.trim();
              if (value && !isValidURL(value)) {
                setFieldErrors(prev => ({ ...prev, prescribing_link: true }));
              }
              // Call parent onBlur for auto-save
              if (onBlur) onBlur();
            }}
            placeholder="https://..."
            disabled={isDisabled}
            className={`link-input ${fieldErrors.prescribing_link ? 'input-error' : ''}`}
            required
          />
          {fieldErrors.prescribing_link && study.prescribing_link && !isValidURL(study.prescribing_link) && (
            <span className="error-message">Please enter a valid URL (e.g., https://example.com)</span>
          )}
        </div>

        {/* Contact Your MSL Link */}
        <div className={`input-group ${fieldErrors.msl_contact_link ? 'error' : ''}`}>
          <label htmlFor={`msl-contact-link-${study.id}`}>
            Contact Your MSL Link
            {fieldErrors.msl_contact_link && <ErrorIcon />}
          </label>
          <input
            type="url"
            id={`msl-contact-link-${study.id}`}
            value={study.msl_contact_link || ''}
            onChange={(e) => {
              onUpdate('msl_contact_link', e.target.value);
              if (e.target.value && isValidURL(e.target.value)) clearFieldError('msl_contact_link');
            }}
            onBlur={(e) => {
              // Validate URL on blur
              const value = e.target.value.trim();
              if (value && !isValidURL(value)) {
                setFieldErrors(prev => ({ ...prev, msl_contact_link: true }));
              }
              // Call parent onBlur for auto-save
              if (onBlur) onBlur();
            }}
            placeholder="https://..."
            disabled={isDisabled}
            className={`link-input ${fieldErrors.msl_contact_link ? 'input-error' : ''}`}
            required
          />
          {fieldErrors.msl_contact_link && study.msl_contact_link && !isValidURL(study.msl_contact_link) && (
            <span className="error-message">Please enter a valid URL (e.g., https://example.com)</span>
          )}
        </div>
        </div>

        {/* {!isDisabled && (
          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary btn-save"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <span className="spinner small"></span>
                  Saving...
                </>
              ) : (
                'Save and Continue'
              )}
            </button>
          </div>
        )} */}
      </form>

      {/* Loading overlay for AI summary generation */}
      {isGeneratingSummary && (
        <div className="loading-overlay">
          <div className="loading-content">
            <span className="spinner large"></span>
            {/* <p>Generating summary from article...</p> */}
            {/* <p className="loading-subtext">This may take a few moments</p> */}
          </div>
        </div>
      )}
    </div>
  );
};

export default StudyForm;
